/**
 * WarrantBadge — small inline pill for stock rows / portfolio rows /
 * modal headers. Renders one of three states based on the warrant's
 * status field (from `/api/warrants/{ticker}` or `stock_cache.warrant_status`):
 *
 *   • "active"                → amber "WARRANT" pill
 *   • "redemption_announced"  → orange "REDEEM SOON" pill (with deadline
 *                                on hover)
 *   • "redeemed"              → rose "REDEEMED" pill
 *
 * All states link into the shared BadgeKey glossary via BadgeHelpIcon
 * so a user who's never seen a warrant can tap it and learn what it
 * means before doing anything destructive.
 *
 * Round P140 (Feb 2026) — companion to services/warrants_pipeline.py
 * on the backend.
 */
import { BadgeHelpIcon } from '@/components/BadgeKey';
import { AlertTriangle, XCircle, FileText, Calculator, X as XIcon, ChevronDown, ChevronUp, Layers } from 'lucide-react';
import { useMemo, useState, useEffect } from 'react';
import WarrantBreakEvenPill from '@/components/WarrantBreakEvenPill';
import WarrantExpiryChip from '@/components/WarrantExpiryChip';
import { ExchangeInfoDialog, ExercisePriceDialog } from '@/components/WarrantInfoModals';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function WarrantBadge({ status, deadline, price, testIdSuffix, className = '', isUnit = false }) {
  const s = (status || 'active').toLowerCase();

  // Oct 2026 — SPAC units (JABRU, CMIIU…) are share + warrant/right
  // bundles, not warrants. Own pill so they're never mistaken for one.
  if (isUnit) {
    return (
      <span
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-teal-500/20 text-teal-800 dark:text-teal-300 ring-1 ring-teal-500/50 ${className}`}
        title="Unit — one bundle of 1 common share + a warrant and/or right under a single ticker (ends in U). Splits into separate tickers ~45–52 days after the IPO. Pays NO dividends."
        data-testid={testIdSuffix ? `unit-badge-${testIdSuffix}` : 'unit-badge'}
      >
        <Layers className="w-2.5 h-2.5" />
        <span>UNIT</span>
        <BadgeHelpIcon badgeKey="unit" testIdSuffix={testIdSuffix} />
      </span>
    );
  }

  if (s === 'redeemed') {
    return (
      <span
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-rose-500/25 text-rose-700 dark:text-rose-300 ring-1 ring-rose-500/60 ${className}`}
        title={
          deadline
            ? `Redeemed on ${deadline}${price != null ? ` at $${price.toFixed(2)}/warrant` : ''}`
            : 'Force-redeemed by the issuer — no longer trading'
        }
        data-testid={testIdSuffix ? `warrant-badge-redeemed-${testIdSuffix}` : 'warrant-badge-redeemed'}
      >
        <XCircle className="w-2.5 h-2.5" />
        <span>REDEEMED</span>
        <BadgeHelpIcon badgeKey="warrant_redeemed" testIdSuffix={testIdSuffix} />
      </span>
    );
  }

  // Round P153 (Jun 2026) — expired warrants (past expiration date,
  // never redeemed). RNWWW case: expired paper trading at $0.00.
  if (s === 'expired') {
    return (
      <span
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-zinc-500/25 text-zinc-600 dark:text-zinc-300 ring-1 ring-zinc-500/60 ${className}`}
        title={
          deadline
            ? `Expired on ${deadline} — these warrants can no longer be exercised and are worthless`
            : 'Past its expiration date — these warrants can no longer be exercised and are worthless'
        }
        data-testid={testIdSuffix ? `warrant-badge-expired-${testIdSuffix}` : 'warrant-badge-expired'}
      >
        <XCircle className="w-2.5 h-2.5" />
        <span>EXPIRED</span>
      </span>
    );
  }

  if (s === 'redemption_announced') {
    return (
      <span
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-orange-500/20 text-orange-700 dark:text-orange-300 ring-1 ring-orange-500/60 ${className}`}
        title={
          deadline
            ? `Redemption 8-K filed — deadline ${deadline}${price != null ? `, tender at $${price.toFixed(2)}` : ''}. Exercise or sell before then.`
            : 'Early redemption 8-K filed — check /warrants for the deadline'
        }
        data-testid={testIdSuffix ? `warrant-badge-soon-${testIdSuffix}` : 'warrant-badge-soon'}
      >
        <AlertTriangle className="w-2.5 h-2.5" />
        <span>REDEEM SOON</span>
        <BadgeHelpIcon badgeKey="warrant_redemption_soon" testIdSuffix={testIdSuffix} />
      </span>
    );
  }

  // Default: active warrant
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-amber-500/20 text-amber-800 dark:text-amber-300 ring-1 ring-amber-500/50 ${className}`}
      title="Public stock warrant — right to buy common stock at a strike price. Pays NO dividends. Watch for early-redemption 8-Ks."
      data-testid={testIdSuffix ? `warrant-badge-active-${testIdSuffix}` : 'warrant-badge-active'}
    >
      <FileText className="w-2.5 h-2.5" />
      <span>WARRANT</span>
      <BadgeHelpIcon badgeKey="warrant" testIdSuffix={testIdSuffix} />
    </span>
  );
}

/**
 * WarrantPnlCalculator — Round P140·5 (Feb 2026)
 *
 * Collapsible P/L widget rendered INSIDE the WarrantPanel (below the
 * exercise-price row) so users don't have to switch tabs to check
 * whether their warrant position is above/below break-even.
 *
 * Three inputs:
 *   • Current warrant price     — auto, from stock.price / stock.current_price
 *   • Exercise (strike) price   — auto, from warrant.exercise_price
 *   • Cost basis                — auto-populated from the user's FIFO
 *     average cost IF they hold it (props.userAvgCost). Users can also:
 *       (a) type any value in the input to override
 *       (b) click "Use my avg cost $X.XX" to explicitly snap back
 *       (c) click "Clear" to reset to the FIFO avg (or blank if
 *           they don't hold any).
 *
 * Six outputs:
 *   • Break-even underlying price = exercise + cost basis
 *   • Intrinsic value             = max(0, current underlying − exercise)
 *   • Time value                  = warrant price − intrinsic
 *   • Unrealized P/L $            = (warrant price − cost basis) × shares
 *   • Unrealized P/L %            = (warrant price − cost basis) / cost basis
 *   • Distance to break-even $/%  = underlying gap needed
 */
function WarrantPnlCalculator({ warrant, currentPrice, underlyingPrice, userAvgCost, userShares }) {
  const [open, setOpen] = useState(true);              // A. always expanded by default
  const [costInput, setCostInput] = useState('');
  const [costOverridden, setCostOverridden] = useState(false);
  // Round P140·5 (Feb 2026) — Best-effort fetch of the underlying's
  // current price for intrinsic value math. Silent 404 for warrants
  // whose underlying isn't in stock_cache (private/pre-IPO SPACs).
  const [fetchedUnderPrice, setFetchedUnderPrice] = useState(null);
  useEffect(() => {
    if (underlyingPrice != null) return;                 // already have it
    const underTkr = warrant?.underlying_ticker;
    if (!underTkr) return;
    let cancelled = false;
    axios
      .get(`${API}/stocks/${encodeURIComponent(underTkr)}`)
      .then((r) => {
        if (cancelled) return;
        const p = r.data?.price ?? r.data?.current_price ?? null;
        if (p != null) setFetchedUnderPrice(p);
      })
      .catch(() => { /* silent — no underlying available */ });
    return () => { cancelled = true; };
  }, [warrant?.underlying_ticker, underlyingPrice]);

  // On mount / when userAvgCost arrives, auto-fill the input UNLESS
  // the user has explicitly overridden it. This mirrors the user's
  // spec: "auto populated from actual position FIFO average" + "Clear
  // button resets back to actual position FIFO avg."
  useEffect(() => {
    if (!costOverridden && userAvgCost != null && userAvgCost > 0) {
      setCostInput(String(userAvgCost.toFixed(4)));
    }
  }, [userAvgCost, costOverridden]);

  const costBasis = useMemo(() => {
    const n = parseFloat(costInput);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [costInput]);

  const exercisePrice = warrant?.exercise_price ?? null;
  const warrantPrice = Number.isFinite(currentPrice) ? currentPrice : null;
  const effectiveUnderPrice = underlyingPrice ?? fetchedUnderPrice;
  const underPrice = Number.isFinite(effectiveUnderPrice) ? effectiveUnderPrice : null;

  // Derived math — all guarded against missing inputs.
  const breakEvenUnderlying = exercisePrice != null && costBasis != null
    ? exercisePrice + costBasis
    : null;
  const intrinsic = exercisePrice != null && underPrice != null
    ? Math.max(0, underPrice - exercisePrice)
    : null;
  const timeValue = warrantPrice != null && intrinsic != null
    ? warrantPrice - intrinsic
    : null;
  const unrealizedPerShare = warrantPrice != null && costBasis != null
    ? warrantPrice - costBasis
    : null;
  const unrealizedPct = unrealizedPerShare != null && costBasis != null
    ? (unrealizedPerShare / costBasis) * 100
    : null;
  const unrealizedTotal = unrealizedPerShare != null && userShares != null && userShares > 0
    ? unrealizedPerShare * userShares
    : null;
  const distanceUnderlying = breakEvenUnderlying != null && underPrice != null
    ? breakEvenUnderlying - underPrice
    : null;
  const distancePct = distanceUnderlying != null && underPrice != null && underPrice > 0
    ? (distanceUnderlying / underPrice) * 100
    : null;

  const handleUseMyAvg = () => {
    if (userAvgCost != null && userAvgCost > 0) {
      setCostInput(String(userAvgCost.toFixed(4)));
      setCostOverridden(false); // snapped back — future auto-fills honored
    }
  };
  const handleClear = () => {
    setCostOverridden(false);
    // effect above will re-auto-fill from FIFO. If no FIFO, blank.
    if (userAvgCost == null || userAvgCost <= 0) setCostInput('');
  };
  const handleCostChange = (e) => {
    setCostInput(e.target.value);
    setCostOverridden(true);
  };

  const fmt$ = (n, d = 2) => n == null ? '—' : `$${n.toFixed(d)}`;
  const fmtPct = (n) => n == null ? '—' : `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
  const gainClass = (n) => n == null ? '' : (n >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400');

  return (
    <div
      className="mt-3 pt-3 border-t border-border/40"
      data-testid="warrant-pnl-calculator"
    >
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-2 group"
        data-testid="warrant-pnl-toggle"
      >
        <span className="inline-flex items-center gap-2">
          <Calculator className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
          <span className="text-base font-bold text-foreground">P/L Calculator</span>
          {unrealizedPct != null && (
            <span className={`text-sm font-mono font-bold ${gainClass(unrealizedPct)}`}>
              {fmtPct(unrealizedPct)}
            </span>
          )}
        </span>
        {open
          ? <ChevronUp className="w-4 h-4 text-muted-foreground group-hover:text-foreground" />
          : <ChevronDown className="w-4 h-4 text-muted-foreground group-hover:text-foreground" />}
      </button>

      {open && (
        <div className="mt-2 space-y-3">
          {/* Cost basis input row — auto-populated + overridable */}
          <div className="rounded-md bg-background/40 border border-border/40 p-2">
            <div className="flex items-center justify-between gap-2 mb-1">
              <label className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                Cost basis / warrant
              </label>
              <div className="flex items-center gap-1">
                {userAvgCost != null && userAvgCost > 0 && (
                  <button
                    type="button"
                    onClick={handleUseMyAvg}
                    className="text-[10px] px-1.5 py-0.5 rounded border border-indigo-500/50 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-500/10"
                    data-testid="warrant-pnl-use-my-avg"
                    title={`Snap to your FIFO average cost of $${userAvgCost.toFixed(4)}`}
                  >
                    Use my avg (${userAvgCost.toFixed(2)})
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleClear}
                  disabled={!costOverridden && !costInput}
                  className="text-[10px] px-1.5 py-0.5 rounded border border-border/60 text-muted-foreground hover:bg-muted/40 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-0.5"
                  data-testid="warrant-pnl-clear"
                  title="Reset back to your FIFO average (or blank if you don't hold any)"
                >
                  <XIcon className="w-2.5 h-2.5" />
                  Clear
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-lg font-mono font-bold text-foreground">$</span>
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                value={costInput}
                onChange={handleCostChange}
                placeholder={userAvgCost != null && userAvgCost > 0 ? String(userAvgCost.toFixed(4)) : "e.g. 1.25"}
                className="flex-1 text-lg font-mono bg-transparent border-b border-border/50 focus:border-indigo-500 outline-none px-1 py-0.5"
                data-testid="warrant-pnl-cost-input"
              />
              {userShares != null && userShares > 0 && (
                <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                  × {userShares.toLocaleString()} warrants
                </span>
              )}
            </div>
            {costOverridden && userAvgCost != null && userAvgCost > 0 && (
              <p className="text-[10px] text-amber-700 dark:text-amber-400 mt-1">
                Overriding your FIFO avg (${userAvgCost.toFixed(4)}). Click Clear to reset.
              </p>
            )}
          </div>

          {/* Result grid — headline numbers bigger than surrounding text */}
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-md bg-background/40 border border-border/40 p-2">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Break-even (underlying)</div>
              <div className="text-lg font-mono font-bold text-foreground" data-testid="warrant-pnl-breakeven">
                {fmt$(breakEvenUnderlying)}
              </div>
              {distanceUnderlying != null && (
                <div className={`text-[11px] font-mono ${gainClass(-distanceUnderlying)}`}>
                  {distanceUnderlying > 0
                    ? `${fmt$(distanceUnderlying)} below break-even`
                    : `${fmt$(Math.abs(distanceUnderlying))} above break-even`}
                  {distancePct != null && (
                    <span className="ml-1 opacity-80">({fmtPct(-distancePct)})</span>
                  )}
                </div>
              )}
            </div>
            <div className="rounded-md bg-background/40 border border-border/40 p-2">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Unrealized P/L</div>
              <div className={`text-lg font-mono font-bold ${gainClass(unrealizedPerShare)}`} data-testid="warrant-pnl-unrealized-per-share">
                {unrealizedPerShare == null
                  ? '—'
                  : `${unrealizedPerShare >= 0 ? '+' : ''}${fmt$(unrealizedPerShare)}/warrant`}
              </div>
              {unrealizedTotal != null && (
                <div className={`text-[11px] font-mono font-semibold ${gainClass(unrealizedTotal)}`} data-testid="warrant-pnl-unrealized-total">
                  {unrealizedTotal >= 0 ? '+' : ''}${Math.abs(unrealizedTotal).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} total
                </div>
              )}
            </div>
            <div className="rounded-md bg-background/40 border border-border/40 p-2">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Intrinsic value</div>
              <div className="text-base font-mono font-bold text-foreground" data-testid="warrant-pnl-intrinsic">
                {fmt$(intrinsic)}
              </div>
              <div className="text-[10px] text-muted-foreground">
                max(0, underlying − strike)
              </div>
            </div>
            <div className="rounded-md bg-background/40 border border-border/40 p-2">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Time value</div>
              <div className="text-base font-mono font-bold text-foreground" data-testid="warrant-pnl-time-value">
                {fmt$(timeValue)}
              </div>
              <div className="text-[10px] text-muted-foreground">
                warrant price − intrinsic
              </div>
            </div>
          </div>

          {(exercisePrice == null || warrantPrice == null) && (
            <p className="text-[10px] text-muted-foreground italic leading-snug">
              {exercisePrice == null && '⚠ No exercise price on file — most SPAC warrants use $11.50; check the SEC filing linked above.'}
              {warrantPrice == null && ' ⚠ Current warrant price unavailable — try refreshing.'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * WarrantPanel — detailed card rendered inside the Stock Modal when
 * the current ticker is a warrant. Shows underlying, exercise price,
 * CIK link to SEC.gov, and (if the 8-K watcher has fired) the
 * redemption deadline + tender price + direct link to the SEC notice.
 *
 * Renders NOTHING when the ticker isn't a warrant (caller passes
 * `null` warrant). Component itself doesn't fetch — the modal passes
 * the pre-loaded warrant doc so we don't round-trip twice.
 */
export function WarrantPanel({ warrant, currentPrice, underlyingPrice, userAvgCost, userShares, onOpenTicker = null }) {
  // Round P142·1 (Feb 2026) — Info-modal state for the two secondary
  // dialogs (Exchange explainer + Exercise-price explainer). These
  // sit above the parent StockModal via shadcn Dialog's z-[200].
  // Hooks MUST be declared before any early return per rules-of-hooks.
  const [exchangeInfoOpen, setExchangeInfoOpen] = useState(false);
  const [exercisePriceOpen, setExercisePriceOpen] = useState(false);

  if (!warrant) return null;
  const s = (warrant.status || 'active').toLowerCase();
  const isRedeemed = s === 'redeemed';
  const isAnnounced = s === 'redemption_announced';

  const cardTone = isRedeemed
    ? 'border-rose-400/50 bg-rose-50/40 dark:bg-rose-950/25'
    : isAnnounced
    ? 'border-orange-400/60 bg-orange-50/50 dark:bg-orange-950/25'
    : 'border-amber-400/50 bg-amber-50/40 dark:bg-amber-950/25';

  const edgarUrl = warrant.cik
    ? `https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=${warrant.cik}&type=8-K`
    : null;

  return (
    <div
      className={`rounded-xl border ${cardTone} p-4 space-y-3`}
      data-testid="stock-modal-warrant-panel"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <FileText className="w-4 h-4 text-amber-700 dark:text-amber-400" />
          <h3 className="text-sm font-bold text-foreground">Stock Warrant</h3>
          <WarrantBadge
            status={warrant.status}
            deadline={warrant.redemption_deadline}
            price={warrant.redemption_price}
            testIdSuffix="modal-panel"
          />
        </div>
      </div>

      <p className="text-xs text-muted-foreground leading-snug">
        {warrant.name || 'Warrant metadata pending Massive ingest.'}
      </p>

      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Underlying stock</div>
          {warrant.underlying_ticker ? (
            <button
              type="button"
              onClick={() => onOpenTicker && onOpenTicker(warrant.underlying_ticker)}
              disabled={!onOpenTicker}
              className={`font-mono font-bold ${onOpenTicker
                ? 'text-blue-600 dark:text-cyan-400 hover:underline focus:outline-none focus:ring-2 focus:ring-violet-400 rounded'
                : 'text-foreground cursor-default'}`}
              data-testid="warrant-panel-underlying"
              title={onOpenTicker ? `Open ${warrant.underlying_ticker} in the stock modal` : undefined}
            >
              {warrant.underlying_ticker}
            </button>
          ) : (
            <div className="font-mono font-bold text-foreground" data-testid="warrant-panel-underlying">—</div>
          )}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Exercise price</div>
          {warrant.exercise_price != null ? (
            <button
              type="button"
              onClick={() => setExercisePriceOpen(true)}
              className="font-mono font-bold text-blue-600 dark:text-cyan-400 hover:underline focus:outline-none focus:ring-2 focus:ring-violet-400 rounded"
              data-testid="warrant-panel-exercise"
              title={`What does exercise price mean?${warrant.terms_source ? ` Sourced from SEC filing ${warrant.terms_source}.` : ''}`}
            >
              {`$${warrant.exercise_price.toFixed(2)}`}
            </button>
          ) : (            <button
              type="button"
              onClick={() => setExercisePriceOpen(true)}
              className="font-mono font-bold text-muted-foreground hover:text-blue-600 dark:hover:text-cyan-400 hover:underline focus:outline-none focus:ring-2 focus:ring-violet-400 rounded"
              data-testid="warrant-panel-exercise"
              title="No exercise price on file — click for context"
            >
              —
            </button>
          )}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Exchange</div>
          {warrant.primary_exchange ? (
            <button
              type="button"
              onClick={() => setExchangeInfoOpen(true)}
              className="font-mono font-semibold text-blue-600 dark:text-cyan-400 hover:underline focus:outline-none focus:ring-2 focus:ring-violet-400 rounded"
              data-testid="warrant-panel-exchange"
              title={`What is ${warrant.primary_exchange}?`}
            >
              {warrant.primary_exchange}
            </button>
          ) : (
            <div className="font-mono font-semibold text-foreground">—</div>
          )}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">CIK (SEC)</div>
          <div className="font-mono font-semibold text-foreground">
            {edgarUrl ? (
              <a
                href={edgarUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="text-blue-600 dark:text-cyan-400 hover:underline"
                data-testid="warrant-panel-edgar-link"
              >
                {warrant.cik} ↗
              </a>
            ) : (
              warrant.cik || '—'
            )}
          </div>
        </div>
      </div>

      {/* Round P141·6 (Feb 2026) — To Break-Even pill. Same clickable
          modal as the Warrant Screener column so mobile users get the
          full context (was previously desktop-hover-only). Silent when
          the warrant doc has no heat_strip yet (price feed pending). */}
      {warrant.heat_strip?.distance_pct != null && (
        <div className="flex items-center gap-2 text-xs">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">To Break-Even</span>
          <WarrantBreakEvenPill warrant={warrant} size="md" />
        </div>
      )}

      {/* Round P141·7 (Feb 2026) — Days-to-expiry chip. Client-side
          countdown from the stored ISO date so it stays fresh even
          across day-boundary refreshes. */}
      {warrant.expiration_date && (
        <div className="flex items-center gap-2 text-xs">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Expires In</span>
          <WarrantExpiryChip expirationDate={warrant.expiration_date} />
          <span className="text-[10px] text-muted-foreground font-mono">{warrant.expiration_date}</span>
        </div>
      )}

      {/* Redemption block — only when the watcher has stamped something. */}
      {(isRedeemed || isAnnounced) && (
        <div className="mt-2 pt-3 border-t border-border/40 space-y-2">
          <div className="flex items-center gap-1.5">
            {isRedeemed ? (
              <XCircle className="w-3.5 h-3.5 text-rose-600 dark:text-rose-400" />
            ) : (
              <AlertTriangle className="w-3.5 h-3.5 text-orange-600 dark:text-orange-400" />
            )}
            <span className="text-xs font-bold text-foreground">
              {isRedeemed ? 'Force-Redeemed' : 'Redemption Announced'}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
            {warrant.redemption_announced_at && (
              <div>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">8-K filed</div>
                <div className="font-mono font-semibold text-foreground">
                  {String(warrant.redemption_announced_at).slice(0, 10)}
                </div>
              </div>
            )}
            {warrant.redemption_deadline && (
              <div>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  {isRedeemed ? 'Redemption date' : 'Deadline'}
                </div>
                <div className="font-mono font-bold text-foreground" data-testid="warrant-panel-deadline">
                  {warrant.redemption_deadline}
                </div>
              </div>
            )}
            {warrant.redemption_price != null && (
              <div>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Tender price</div>
                <div className="font-mono font-bold text-rose-700 dark:text-rose-400" data-testid="warrant-panel-tender-price">
                  ${warrant.redemption_price.toFixed(2)} / warrant
                </div>
              </div>
            )}
          </div>
          {warrant.redemption_notice_url && (
            <a
              href={warrant.redemption_notice_url}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1 text-[11px] text-blue-600 dark:text-cyan-400 hover:underline mt-1"
              data-testid="warrant-panel-notice-link"
            >
              Read the SEC filing ↗
            </a>
          )}
        </div>
      )}

      {!isRedeemed && !isAnnounced && (
        <p className="text-[10px] text-muted-foreground/80 italic leading-snug">
          No redemption notice detected. Our weekly 8-K watcher runs every Friday and stamps this card if the issuer files a redemption 8-K.
        </p>
      )}

      {/* Round P140·5 (Feb 2026) — P/L Calculator. Hidden for redeemed
          warrants (position is closed — no P/L to compute). */}
      {!isRedeemed && (
        <WarrantPnlCalculator
          warrant={warrant}
          currentPrice={currentPrice}
          underlyingPrice={underlyingPrice}
          userAvgCost={userAvgCost}
          userShares={userShares}
        />
      )}

      {/* Round P142·1 (Feb 2026) — Info dialogs. Both use shadcn
          Dialog which lays above the StockModal (z-[200] vs z-[150])
          and auto-closes on outside-click / Esc / X-button. */}
      <ExchangeInfoDialog
        code={warrant.primary_exchange}
        open={exchangeInfoOpen}
        onOpenChange={setExchangeInfoOpen}
      />
      <ExercisePriceDialog
        price={warrant.exercise_price}
        open={exercisePriceOpen}
        onOpenChange={setExercisePriceOpen}
      />
    </div>
  );
}
